import React, { useRef, useState, useEffect } from "react";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Slide from "@mui/material/Slide";
import { useDrag, useDrop } from "react-dnd";
import { Button, List, ListItem } from "@mui/material";
import {
  UpDownArrowIcon,
  CloseIcon,
  Survey,
} from "../../../../assets/common-svg/CommonSvg";
import { useDispatch, useSelector } from "react-redux";
import { listingByProcessId, selectedProcess, updateSequence } from "../../../slices/help";
import InfiniteScroll from "react-infinite-scroll-component";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: 300, sm: 400, md: 500, lg: 400 },
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "10px",
  py: "15px",
  maxHeight: {
    xs: "95vh",
    sm: "95vh",
    md: "95vh",
    lg: "90vh",
    xl: "95vh",
  },
};

const ItemType = "FORM_ITEM";

export default function UpdateHelpSequence({
  open,
  setOpen,
  page,
  maxPages,
  selectProcess,
}) {
  const proccesId = useSelector(selectedProcess);
  const dispatch = useDispatch();

  // Initialize sequenceProcess with selectProcess and manage local pagination
  const [sequenceProcess, setSequenceProcess] = useState(selectProcess);
  const [localPage, setLocalPage] = useState(page);
  const [isDragging, setIsDragging] = useState(false); // Track drag state

  // Update sequenceProcess when selectProcess changes (initial load or reset)
  useEffect(() => {
    setSequenceProcess(selectProcess);
    setLocalPage(page);
  }, [selectProcess, page]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmitEvent = async () => {
    const payload = {
      sequences: {
        sequences: sequenceProcess.map((item, index) => ({
          id: item.id,
          sequence: sequenceProcess.length - index,
        })),
      },
      process_id: proccesId.id,
    };
    await dispatch(updateSequence(payload));
    dispatch(
      listingByProcessId({
        params: {
          id: proccesId.id,
          status: -1,
          page: 1,
          per_page: 10,
          search: "",
        },
        fromModal: false,
      })
    )
    setOpen(false);
  };

  const moveFormItem = (draggedId, targetId) => {
    const draggedIndex = sequenceProcess.findIndex((form) => form.id === draggedId);
    const targetIndex = sequenceProcess.findIndex((form) => form.id === targetId);

    if (draggedIndex === targetIndex || draggedIndex === -1 || targetIndex === -1) return; // Prevent invalid updates

    const updatedFormType = [...sequenceProcess];
    const [draggedItem] = updatedFormType.splice(draggedIndex, 1);
    updatedFormType.splice(targetIndex, 0, draggedItem);
    setSequenceProcess(updatedFormType);
  };

  const handleLoadMore = async () => {
    if (localPage >= maxPages || isDragging) return; // Prevent loading during drag
    const nextPage = localPage + 1;
    try {
      const response = await dispatch(
        listingByProcessId({
          params: {
            id: proccesId.id,
            status: -1,
            page: nextPage,
            per_page: 10,
            search: "",
          },
          fromModal: true,
        })
      ).unwrap();
      setSequenceProcess((prev) => {
        const newItems = response.data.data.items.filter(
          (newItem) => !prev.some((item) => item.id === newItem.id)
        );
        return [...prev, ...newItems];
      });
      setLocalPage(nextPage);
    } catch (err) {
      console.error("Failed to load more:", err);
    }
  };

  const DraggableListItem = React.memo(({ res, index }) => {
    const ref = useRef(null);

    const [{ isDragging: itemDragging }, drag] = useDrag({
      type: ItemType,
      item: { id: res?.id, index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    // Update isDragging state based on drag status
    useEffect(() => {
      setIsDragging(itemDragging);
    }, [itemDragging]);

    const [, drop] = useDrop({
      accept: ItemType,
      hover: (item, monitor) => {
        if (!ref.current || item.id === res?.id) return;

        const clientOffset = monitor.getClientOffset();
        const container = ref.current.closest(".overflow-auto");
        if (!container || !clientOffset) return;

        const { top, bottom } = container.getBoundingClientRect();
        const SCROLL_THRESHOLD = 50;

        if (clientOffset.y < top + SCROLL_THRESHOLD) {
          container.scrollBy({ top: -15, behavior: "smooth" });
        } else if (clientOffset.y > bottom - SCROLL_THRESHOLD) {
          container.scrollBy({ top: 15, behavior: "smooth" });
        }

        moveFormItem(item.id, res?.id);
      },
    });

    drag(drop(ref));

    return (
      <ListItem
        className="border-b border-gray-300 items-center cursor-grab group active:cursor-grabbing"
        key={res.id}
        ref={ref}
        style={{
          cursor: "move",
          opacity: itemDragging ? 0.5 : 1,
          transform: itemDragging ? "scale(1.05)" : "scale(1)",
          transition: "all 0.2s ease",
        }}
      >
        <div className="group-hover:fill-sky-600 mr-4">
          <UpDownArrowIcon />
        </div>
        <div>
          <p className="text-base">{res.title}</p>
        </div>
      </ListItem>
    );
  });

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={open}>
          <Slide direction="left" in={open} mountOnEnter unmountOnExit>
            <div className="relative h-full">
              <Box sx={style} className="w-[700px]">
                <div
                  onClick={handleClose}
                  className="absolute right-0 1sm:right-[-30px] top-[-30px] cursor-pointer"
                >
                  <Tooltip title="close">
                    <div>
                      <CloseIcon />
                    </div>
                  </Tooltip>
                </div>
                <div className="flex w-full p-4">
                  <div className="flex-1">
                    <div className="pb-2 border-b border-gray-300">
                      <Typography
                        id="transition-modal-title"
                        variant="h6"
                        textAlign="start"
                        component="h6"
                      >
                        Update Sequence
                      </Typography>
                    </div>
                    <div className="py-2">
                        <p className="bg-[#FFEDCB] text-[#FB9D2C] p-2 rounded">
                          <span className="font-medium">Note : </span>
                          <span className="text-xs">
                            Drag & Drop Titles To Update Sequence
                          </span>
                        </p>
                      </div>
                    <div
                      className="max-h-[60vh] overflow-auto"
                      id="scrollableDiv"
                    >
                      
                      {sequenceProcess?.length > 0 ? (
                        <InfiniteScroll
                          dataLength={sequenceProcess.length}
                          next={handleLoadMore}
                          hasMore={localPage < maxPages}
                          scrollableTarget="scrollableDiv"
                        >
                          <List className="bg-gray-100 h-100">
                            {sequenceProcess?.map((res, index) => (
                              <DraggableListItem
                                key={res.id}
                                res={res}
                                index={index}
                              />
                            ))}
                          </List>
                        </InfiniteScroll>
                      ) : (
                        <div className="bg-gray-100 flex flex-col items-center justify-center text-center h-full p-5">
                          <Survey />
                          <p className="text-xs">No Data to Display</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <Button
                    variant="contained"
                    type="submit"
                    size="large"
                    onClick={handleSubmitEvent}
                    className="justify-center w-25 m-1"
                  >
                    Update
                  </Button>
                </div>
              </Box>
            </div>
          </Slide>
        </Fade>
      </Modal>
    </div>
  );
}